// Third Party
import React from 'react';
import api from '@/redux/redux-api';

// Assets
import emptyComparisonsImage from '@/images/design-system-v2/empty-state/emptyComparisons.png';

// Components
import ActionsButton, { buttonAction } from '@/views/components/buttons/ActionsButton';
import { ComparisonsTable } from '../../../components/tables';
import { LoadingSpinner } from '../../../components/common';
import { SearchInput } from '../../../components/inputs';
import { ComparisonsTableFields } from '../../../components/tables/ComparisonsTable';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import TablePageLayout from '../../layouts/TablePageLayout';

// Constants
import { MAX_VISIBLE_COMPARISONS, MAX_LOADED_COMPARISONS, NAV_PATHS } from '@/utils/constants';

// Enum
import { COMPARISONS_FILTER } from './ComparisonsFilterNavigation';
import { PAGE_POSITIONS } from '@/enums/TablePageLayout';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Legacy Component
import LegacyComparisonsSection from './LegacyComparisonsSection';

// Libraries
import debounce from 'lodash/debounce';

// Types
import User from '@/types/user';
import ReducedComparison from '@/types/reduced-comparison';
import GenericContact from '@/types/generic-contact';
import Comparison from '@/types/comparison';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';

type Props = {
  className?: string;
  comparisons: ReducedComparison[];
  filterComponent?: JSX.Element;
  hasMore?: boolean;
  hasNoComparisons: boolean;
  i18nTitle?: string;
  responsive?: boolean;
  searchPlaceholder: string;
  selectedFilter: COMPARISONS_FILTER;
  showCreateComparison: boolean;
  forceHideEmptyStateCard?: boolean;
  showHeader?: boolean;
  showStartBMOComparison: boolean;
  sortable?: boolean;
  tableFields?: ComparisonsTableFields[];
  title: string;
  user: User;
  loadComparisons: (query?: string, useNextToken?: boolean) => Promise<ReducedComparison[]>;
  onCreateBMOComparison?: () => void;
  onCreateComparison?: () => void;
  onDeleteComparison: (comparison: ReducedComparison) => void;
  onOpenComparison: (comparison: ReducedComparison) => void;
  onSaveComparison: (id: string, name: string, contact?: GenericContact) => Promise<Comparison>;
};

type LegacyProps = {
  openingComparisonIds?: string[];
};

type State = {
  loading: boolean;
  showMore?: boolean;
};

class ComparisonsSectionV2 extends React.Component<Props, State> {
  // Set default props
  static defaultProps = {
    showCreateComparison: false,
    showStartBMOComparison: false,
  };

  private searchInputRef: SearchInput | null = null;

  private _ismounted: boolean = false;

  private i18nEmptyStatePath = 'EmptyStateCard.Comparisons';

  debounceSearch = debounce(() => {
    const query = this.getSearchQuery();

    this.props.loadComparisons(query).then(() => {
      this.setState({ loading: false });
    });
  }, 300);

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  componentDidMount() {
    this._ismounted = true;
    this.loadComparisons();
  }

  componentWillUnmount() {
    this._ismounted = false;
  }

  getSearchQuery = () => {
    if (!this.searchInputRef) {
      return '';
    }

    return this.searchInputRef.getInputValue();
  };

  loadComparisons = async (useNextToken?: boolean) => {
    const query = this.getSearchQuery();

    this.setState({ loading: true });

    await this.props.loadComparisons(query, useNextToken);

    if (this._ismounted) {
      // TODO This is needed to fix the no-op issue, but we shouldn't be calling setState on unmounted components
      const { responsive, comparisons } = this.props;

      this.setState({
        loading: false,
        showMore: responsive && comparisons.length > MAX_VISIBLE_COMPARISONS,
      });
    }
  };

  onLoadMore = () => {
    this.loadComparisons(true);
  };

  searchComparisons = () => {
    const { loading } = this.state;

    if (!loading) {
      this.setState({ loading: true });
    }

    this.debounceSearch();
  };

  getActionsForButton = (): buttonAction[] => {
    const { onCreateComparison, showStartBMOComparison, onCreateBMOComparison } = this.props;
    return [
      {
        condition: this.showCreateComparisonButton(),
        'data-testid': 'comparisonHome-primaryButton-startNewComparison',
        icon: 'add',
        node: <FormattedMessage id="Comparison.CreateComparison" />,
        onClick: onCreateComparison,
      },
      {
        condition: showStartBMOComparison,
        'data-testid': 'comparisonHome-startBMO',
        icon: 'bmo',
        node: <FormattedMessage id="Comparison.StartBMOGIF" />,
        onClick: onCreateBMOComparison,
      },
    ];
  };

  showCreateComparisonButton = () => this.props.showCreateComparison && !this.showEmptyStateCard();

  showEmptyStateCard = () => {
    const { forceHideEmptyStateCard, hasNoComparisons, selectedFilter } = this.props;
    const noCurrentSearchQuery = !this.getSearchQuery();
    const filterIsNotCompliance = selectedFilter !== COMPARISONS_FILTER.COMPLIANCE;

    // this condition is only to avoid flicking the factSet empty state when we are debouncing the search query
    // and also, for regular appMode, to flicking the button when we should display the empty state card
    const wontFlick = !this.state.loading || !forceHideEmptyStateCard;

    return hasNoComparisons && filterIsNotCompliance && noCurrentSearchQuery && wontFlick;
  };

  showSearchInput = () => {
    const { comparisons, forceHideEmptyStateCard } = this.props;
    const tableHasData = !!comparisons.length;
    const hasCurrentSearchQuery = this.getSearchQuery();

    // this condition is only to avoid flicking the factSet empty state when we are debouncing the search query
    // and also, for regular appMode, to avoid flicking the search input when we should display the empty state card
    const wontFlick = this.state.loading && forceHideEmptyStateCard;

    return tableHasData || hasCurrentSearchQuery || wontFlick;
  };

  renderTable = () => {
    const { comparisons, hasMore, responsive, selectedFilter, sortable, tableFields, user, onDeleteComparison, onOpenComparison, onSaveComparison } =
      this.props;

    return (
      <ComparisonsTable
        user={user}
        fields={tableFields}
        comparisons={comparisons}
        onLoadMore={this.onLoadMore}
        hasMore={hasMore && comparisons.length > MAX_LOADED_COMPARISONS - 1}
        onOpenComparison={onOpenComparison}
        onDeleteComparison={onDeleteComparison}
        onSaveComparison={onSaveComparison}
        responsive={responsive}
        sortable={sortable}
        selectedFilter={selectedFilter}
      />
    );
  };

  renderSearchInput = () => {
    const { searchPlaceholder } = this.props;
    return (
      <>
        {this.showSearchInput() && (
          <SearchInput
            compact
            className="search-input-v2"
            ref={ref => {
              this.searchInputRef = ref;
            }}
            loading={this.state.loading}
            placeholder={searchPlaceholder}
            onChange={this.searchComparisons}
            data-private
            data-dd-privacy="mask"
          />
        )}
      </>
    );
  };

  renderSpinner = () => (
    <div className="loading-spinner-container">
      <LoadingSpinner className="content-spinner" message={formatMessage('ComparisonHome.Loading')} />
    </div>
  );

  renderEmptyState = () => {
    const primaryButton = {
      label: `${this.i18nEmptyStatePath}.PrimaryButtonText`,
      onClick: () => {
        api.getHistory()?.push(NAV_PATHS.COMPARISON);
      },
    };

    return (
      <>
        {this.showEmptyStateCard() ? (
          // This case is for having an empty table when we have no comparisons at all in draft, assigned, or recent (compliance is not included)
          // for FactSet, we never show the EmptyStateCard (forceHideEmptyStateCard is always true)
          <>
            {!this.props.forceHideEmptyStateCard && (
              <EmptyStateCard
                imageAlt="Comparisons example image"
                imagePath={emptyComparisonsImage}
                messagePath={`${this.i18nEmptyStatePath}.Message`}
                primaryButton={primaryButton}
                titlePath={`${this.i18nEmptyStatePath}.Title`}
              />
            )}
          </>
        ) : (
          // This case is for having an empty table when we have some comparisons, but none for the selected filter
          <ComparisonsTable sortable={false} comparisons={[]} comparisonSearchQuery={this.searchInputRef?.getInputValue()} />
        )}
      </>
    );
  };

  render() {
    const { className, comparisons, forceHideEmptyStateCard } = this.props;

    const { loading } = this.state;

    const showLoadingSpinner = loading;

    return (
      <TablePageLayout
        mainClassName={className}
        pageTitle={this.props.i18nTitle}
        // Page Elements
        loadingElement={this.renderSpinner()}
        noDataElement={this.renderEmptyState()}
        pageActionsElement={<ActionsButton actions={this.getActionsForButton()} />}
        pillsFilterElement={this.props.filterComponent}
        searchInputElement={this.renderSearchInput()}
        tableElement={this.renderTable()}
        // Logic
        isLoading={showLoadingSpinner && !comparisons.length}
        hasData={comparisons.length !== 0}
        // Page options
        showHeader={this.props.showHeader}
        // Note on showPage : here we want to hide the entire page for factSet,
        // so we don't show the entire page layout when we have no comparisons
        showPage={!(forceHideEmptyStateCard && this.showEmptyStateCard())}
        showSubHeader={false}
        searchInputPosition={PAGE_POSITIONS.TOP_RIGHT_PAGE}
        pageActionsPosition={PAGE_POSITIONS.TOP_RIGHT_PAGE}
      />
    );
  }
}

const ComparisonsSection = (props: Props & LegacyProps) => {
  const { isLightVersion } = useThemeVersion();

  if (isLightVersion) {
    return <ComparisonsSectionV2 {...props} />;
  }

  return <LegacyComparisonsSection {...props} />;
};

export default ComparisonsSection;
